import Button from '@mui/material/Button';
import { useSnackbar } from 'notistack';
import React from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
  Membership,
  ResponseListMemberships,
  useDeleteOrganisationMemberships,
  useListOrganisationMembers,
  UserSummary,
} from '../../../../app/ApiGen';
import { useCurrentOrganisation } from '../../../../app/CurrentOrganisationProvider';
import { RefreshButton } from '../../../../components/DataTable/Toolbar';
import { DataErrorHandler, ErrorHandler } from '../../../../components/ErrorHandler';
import { ExportCsv, ExportProps } from '../../../../components/ExportCsv';
import { TableLink } from '../../../../components/Link';
import {
  Card,
  ColumnChanger,
  ColumnChangerProps,
  DataTable,
  Loading,
  SearchField,
  SearchFieldProps,
  Workspace,
} from '../../../../design-system';
import type { SubState } from '../../../../store/types';
import type { Column } from '../../../../utils/dataTable/columns';
import { navigateOnRowClick } from '../../../../utils/dataTable/rowHelpers';
import { normalise, Normalised } from '../../../../utils/request';
import { userRoleLabels } from '../../../../utils/users/FieldDefinitions';
import { getDeleteUserAction } from '../../../../utils/users/form';

type OrgUser = Normalised<UserSummary> & { role: string };
function toRowData(rows: OrgUser[], membership: Normalised<Membership>): OrgUser[] {
  const { user, role } = membership;
  if (!user || !('email' in user)) {
    return rows;
  }
  return rows.concat({ role: userRoleLabels[role], ...user });
}

const getToolbarProps: (
  organisationSlug: string,
  refetch: () => Promise<ResponseListMemberships | null>,
  loading: boolean,
) => {
  search: (props: React.PropsWithChildren<SearchFieldProps>) => JSX.Element;
  actions: (props: React.PropsWithChildren<ColumnChangerProps> & ExportProps<OrgUser>) => JSX.Element;
} = (organisationSlug, refetch, loading) => ({
  search: ({ dataManager, search, onSearchChanged }: SearchFieldProps) => (
    <SearchField dataManager={dataManager} item="users" onSearchChanged={onSearchChanged} search={search} />
  ),
  actions: (props: React.PropsWithChildren<ColumnChangerProps> & ExportProps<OrgUser>) => {
    const { columnsButton, columns, data, getFieldValue, icons, onColumnsChanged } = props;
    return (
      <>
        <ColumnChanger
          columnsButton={columnsButton}
          columns={columns}
          icons={icons}
          onColumnsChanged={onColumnsChanged}
        />
        <RefreshButton refetch={refetch} isLoading={loading} />
        <ExportCsv<OrgUser>
          columns={columns}
          data={data}
          exportFileName="users"
          getFieldValue={getFieldValue}
          icons={props.icons}
        />
        <Button color="primary" component={Link} to={`/organisations/${organisationSlug}/add/user`} variant="contained">
          Add user
        </Button>
      </>
    );
  },
});

const WithContext: React.FC<{ organisation: SubState<'organisation'> }> = ({ organisation }) => {
  const history = useHistory();

  const { data, error, loading, refetch } = useListOrganisationMembers({ organisationId: organisation.id });
  const removeUsers = useDeleteOrganisationMemberships({ organisationId: organisation.id });

  const { closeSnackbar, enqueueSnackbar } = useSnackbar();

  const memberships: Membership[] = data?.data ?? [];
  const tableData: OrgUser[] = React.useMemo(() => (data ? normalise(data).reduce(toRowData, []) : []), [data]);

  const userOverviewUrl = React.useCallback(
    (user: OrgUser) => `/organisations/${organisation.slug}/users/${user.id}/overview`,
    [organisation.slug],
  );

  const columns = React.useMemo<Column<OrgUser>[]>(
    () => [
      {
        field: 'name',
        title: 'Name',
        render: (rowData) => <TableLink to={userOverviewUrl(rowData)}>{rowData.name}</TableLink>,
      },
      { field: 'email', title: 'Email' },
      { field: 'role', title: 'Role' },
    ],
    [userOverviewUrl],
  );

  if (error) {
    return <DataErrorHandler description="Unable to load users" error={error} refetch={refetch} />;
  }

  return (
    <Workspace maxWidth="xl">
      <Card>
        <DataTable<OrgUser>
          isLoading={loading}
          options={{ columnsButton: true, actionsColumnIndex: -1, loadingType: 'linear' }}
          toolbarProps={getToolbarProps(organisation.slug, refetch, loading)}
          columns={columns}
          data={tableData}
          onRowClick={navigateOnRowClick(userOverviewUrl, history)}
          actions={[
            getDeleteUserAction({
              closeSnackbar,
              enqueueSnackbar,
              refetch,
              removeUser: async (user) => {
                const membership = memberships.find((m) => user.id === m.relationships.user.data.id);
                if (!membership) {
                  return;
                }
                await removeUsers.mutate({ data: [{ id: membership.id, type: 'memberships' }] });
              },
              source: 'the organisation',
            }),
          ]}
        />
      </Card>
    </Workspace>
  );
};

const UsersListView: React.FC = () => {
  const { organisation, loading } = useCurrentOrganisation();

  if (loading) {
    return (
      <Workspace>
        <Loading />
      </Workspace>
    );
  }

  if (!organisation) {
    return (
      <Workspace>
        <ErrorHandler description="Unable to load organisation" />
      </Workspace>
    );
  }

  return <WithContext organisation={organisation} />;
};

export default UsersListView;
